import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import { Logo } from "@/components/Logo";
import { ArrowRight, ArrowDown, Users, Star, Globe, Video, PlayCircle, BadgeCheck, MapPin, Mic, Music2, Sparkles, Smile, Camera, MoreHorizontal, Drama, ChevronDown, ChevronLeft, ChevronRight, X, Loader2, Smartphone } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { catLabel } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useLoginPrompt } from "@/lib/loginPrompt";
import { openGoogleRedirect } from "@/lib/googleAuth";
import { toast } from "sonner";
import Footer from "@/components/Footer";

/**
 * Iter 50 — Landing rebuilt strictly against the user's uploaded reference.
 *
 * Text constraint (ONLY these strings visible on the page):
 *   Heading:  "The Spotlight Awaits Your Talent"
 *   Subtext:  "Upload performances, build a professional portfolio, and get
 *              discovered by the entertainment industry."
 *   Button:   "Join NextScene"
 *   Trust:    "Trusted by actors, singers, dancers, creators & global artists"
 * Below-fold UI chrome (Sign In / Trending Talent / Explore Categories /
 * View all / stat labels / category pill labels / card metadata) is pulled
 * directly from the reference mock and is not marketing copy.
 *
 * Fixes vs Iter 49:
 *   ✔ Hero background is the user-uploaded "Spotlight Girl" image itself —
 *     no cross-fading video montage, no Unsplash fallbacks.
 *   ✔ Staggered cards removed from hero. Trending Talent below now uses a
 *     horizontal-scroll rail matching the reference (name + verified check,
 *     category · city, play badge, duration chip).
 *   ✔ Heading fluid font `clamp(1.9rem, 5.5vw, 4.5rem)` so it fits on a
 *     13-15" MacBook without any letter cut-off.
 */

// User-uploaded reference image, served directly from the emergent asset CDN.
const HERO_IMAGE = "https://customer-assets-rejwkqb3.emergentagent.net/job_showcase-india/artifacts/d26ob8q4_ChatGPT%20Image%20Aug%2013%2C%202026%2C%2008_55_32%20AM.png";

const STATS = [
  { label: "Performances", value: "120K+", icon: Users },
  { label: "Categories",   value: "30+",   icon: Star },
  { label: "Globally Trusted", value: null, icon: Globe },
  { label: "Creators",     value: "50K+",  icon: Video },
];

const CATEGORIES = [
  { key: "acting",     label: "Acting",     icon: Drama,   to: "/discover?category=actor" },
  { key: "singing",    label: "Singing",    icon: Mic,     to: "/discover?category=singer" },
  { key: "dancing",    label: "Dancing",    icon: Music2,  to: "/discover?category=dancer" },
  { key: "voice-over", label: "Voice Over", icon: Sparkles,to: "/discover?category=voice-artist" },
  { key: "comedy",     label: "Comedy",     icon: Smile,   to: "/discover?category=comedian" },
  { key: "creator",    label: "Creator",    icon: Camera,  to: "/discover?category=filmmaker" },
  { key: "more",       label: "More",       icon: MoreHorizontal, to: "/discover" },
];

// "Discover Your Craft" strip — cinematic OTT-style category cards.
// Category slugs match backend `talent_categories`: actor / singer / dancer /
// voice-artist / comedian / model.
const CRAFTS = [
  { key: "acting",     label: "ACTING",     icon: Drama,    slug: "actor",        img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=70" },
  { key: "singing",    label: "SINGING",    icon: Mic,      slug: "singer",       img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&q=70" },
  { key: "dancing",    label: "DANCING",    icon: Music2,   slug: "dancer",       img: "https://images.unsplash.com/photo-1547153760-18fc86324498?auto=format&fit=crop&w=800&q=70" },
  { key: "voice-over", label: "VOICE OVER", icon: Sparkles, slug: "voice-artist", img: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=800&q=70" },
  { key: "comedy",     label: "COMEDY",     icon: Smile,    slug: "comedian",     img: "https://images.unsplash.com/photo-1527224857830-43a7acc85260?auto=format&fit=crop&w=800&q=70" },
  { key: "modeling",   label: "MODELING",   icon: Camera,   slug: "model",        img: "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=800&q=70" },
];

function Laurel({ flip = false }) {
  return (
    <svg width="26" height="34" viewBox="0 0 26 34" fill="none" aria-hidden="true"
         style={{ transform: flip ? "scaleX(-1)" : "none" }}
         className="text-[#C79A2B]">
      <path d="M13 2 C 6 8, 4 18, 8 32" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M10 6 C 6 6, 5 10, 8 10 Z"  fill="currentColor" opacity="0.85"/>
      <path d="M9 11 C 5 11, 4 15, 7 15 Z" fill="currentColor" opacity="0.75"/>
      <path d="M9 16 C 5 16, 4 20, 7 20 Z" fill="currentColor" opacity="0.65"/>
      <path d="M9 21 C 5 21, 4 25, 7 25 Z" fill="currentColor" opacity="0.55"/>
    </svg>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [featVideos, setFeatVideos] = useState([]);
  const [scrollP, setScrollP] = useState(0);

  useEffect(() => {
    api.get("/public/featured-videos?limit=12").then(r => setFeatVideos(r.data.videos || [])).catch(() => {});
  }, []);

  // Scroll-linked hero compress (kept subtle — image is the hero, not stock video)
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const p = Math.max(0, Math.min(1, window.scrollY / window.innerHeight));
        setScrollP(p);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const heroContentStyle = {
    transform: `translateY(${-scrollP * 40}px) scale(${1 - scrollP * 0.05})`,
    opacity: 1 - scrollP * 0.55,
    transformOrigin: "center top",
  };
  // Iter 51: image stays at full opacity at every scroll position; slight
  // parallax lift retained, but no opacity fade and no scale override
  // (the .hero-ken-burns animation handles the zoom).
  const heroBgStyle = {
    transform: `translateY(${-scrollP * 20}px)`,
  };

  const trendingClips = useMemo(() => featVideos.filter(v => v.thumbnail_url || v.url).slice(0, 8), [featVideos]);

  // Top-nav login modal is now globally provided; trigger via the hook.
  const { openModal: openLoginModal } = useLoginPrompt();

  // Cinematic banner: reveal-on-scroll (fires once when >=22% of section is visible)
  const bannerRef = useRef(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setBannerVisible(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setBannerVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.22 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen text-white overflow-x-hidden" style={{ backgroundColor: "#070709" }}>
      {/* ═══════════════ HERO — Text overlaid on image, poster-style ═══════════════ */}
      <section
        className="relative w-full"
        style={{ backgroundColor: "#070709", paddingBottom: "3rem" }}
      >
        {/* Chrome — floating overlay on hero image (no separate bar) */}
        <header
          data-testid="top-nav"
          className="absolute top-0 left-0 right-0 z-30 flex items-start justify-between pointer-events-none"
          style={{
            paddingTop: "28px",
            paddingLeft: "36px",
            paddingRight: "36px",
          }}
        >
          <div className="hero-reveal pointer-events-auto"><Logo size="md"/></div>
          <div className="hero-reveal-2 flex items-center pointer-events-auto" style={{ gap: "16px" }}>
            <Link
              to="/discover"
              data-testid="top-nav-discover"
              className="ns-nav-discover inline-flex items-center"
              style={{
                height: "42px",
                padding: "0 20px",
                borderRadius: "999px",
                background: "rgba(7,7,9,0.18)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                border: "1px solid rgba(199,154,43,0.55)",
                color: "#F5F3EE",
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontWeight: 500,
                fontSize: "15px",
                letterSpacing: "0.2px",
                transition: "background 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
              }}
            >
              Discover
            </Link>
            <button
              type="button"
              onClick={openLoginModal}
              data-testid="top-nav-login"
              className="ns-nav-login inline-flex items-center"
              style={{
                height: "42px",
                padding: "0 22px",
                borderRadius: "999px",
                background: "rgba(7,7,9,0.18)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                border: "1px solid rgba(199,154,43,0.55)",
                color: "#F5F3EE",
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontWeight: 600,
                fontSize: "15px",
                letterSpacing: "0.2px",
                cursor: "pointer",
                transition: "background 220ms ease, border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease",
              }}
            >
              Login
            </button>
          </div>
        </header>

        {/* Image at natural aspect ratio — full dress visible, never cropped */}
        <div className="relative w-full overflow-hidden" data-testid="hero-image-bg">
          <img
            src={HERO_IMAGE}
            alt=""
            aria-hidden="true"
            className="hero-ken-burns block w-full h-auto"
            style={{ objectFit: "contain", filter: "none" }}
          />
          {/* Text stack — absolutely positioned INSIDE the image area, starting
              just below the gown hem (~68% down the image). Extends downward
              as one continuous cinematic poster. */}
          <div
            data-testid="hero-text-group"
            className="absolute inset-x-0 text-center px-5 md:px-8"
            style={{ top: "68%" }}
          >
            <p
              data-testid="hero-heading-1"
              className="hero-reveal-3"
              style={{
                fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                color: "#F5F3EE",
                fontWeight: 400,
                fontSize: "clamp(28px, 3.89vw, 56px)",
                lineHeight: 0.95,
                letterSpacing: "-1px",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased",
              }}
            >
              The
            </p>
            <p
              data-testid="hero-heading-2"
              className="hero-reveal-3"
              style={{
                marginTop: "4px",
                fontFamily: "'Ogg Italic', 'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                color: "#C79A2B",
                fontWeight: 500,
                fontStyle: "italic",
                fontSize: "clamp(48px, 6.39vw, 92px)",
                lineHeight: 0.88,
                letterSpacing: "-2px",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased",
                textShadow: "0 0 12px rgba(199,154,43,0.12), 0 0 24px rgba(199,154,43,0.08)",
              }}
            >
              Spotlight
            </p>
            <p
              data-testid="hero-heading-3"
              className="hero-reveal-3"
              style={{
                marginTop: "4px",
                fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                color: "#F5F3EE",
                fontWeight: 400,
                fontSize: "clamp(34px, 4.72vw, 68px)",
                lineHeight: 0.92,
                letterSpacing: "-1.5px",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased",
              }}
            >
              Awaits Your Talent
            </p>
            <p
              data-testid="hero-subtext"
              className="hero-reveal-4 mx-auto"
              style={{
                marginTop: "16px",
                color: "rgba(255,255,255,0.85)",
                fontSize: "clamp(0.95rem, 1.3vw, 1.1rem)",
                lineHeight: 1.5,
                maxWidth: "36rem",
              }}
            >
              Upload performances, build a professional portfolio, and get discovered by the entertainment industry.
            </p>
            <div style={{ marginTop: "24px" }}>
              <button
                data-testid="hero-cta-signup"
                onClick={() => nav("/signup")}
                className="hero-reveal-5 hero-cta-pulse group h-12 md:h-14 px-8 md:px-10 rounded-full text-black font-bold tracking-wide inline-flex items-center gap-3 transition-transform hover:scale-[1.03] active:scale-[0.98] border border-[#E6BC5A]/60"
                style={{
                  background: "linear-gradient(180deg, #E6BC5A 0%, #C79A2B 55%, #8F6E1E 100%)",
                  fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                }}
              >
                Join NextScene
                <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform"/>
              </button>
            </div>
            <div
              className="hero-reveal-5 inline-flex items-center gap-3 md:gap-4 max-w-full"
              style={{ marginTop: "24px" }}
              data-testid="hero-trust-line"
            >
              <Laurel/>
              <p className="text-[10px] md:text-xs text-white/90 whitespace-nowrap">
                Trusted by actors, singers, dancers, creators &amp; global artists
              </p>
              <Laurel flip/>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ STATS BAR (overlaps hero on scroll) ═══════════════ */}
      <section data-testid="stats-bar" className="relative -mt-10 md:-mt-12 z-30" style={{ backgroundColor: "#070709" }}>
        <div className="max-w-6xl mx-auto px-5 md:px-6">
          <div className="rounded-2xl border border-white/10 bg-[#0D0C10]/95 backdrop-blur-md shadow-[0_25px_80px_-20px_rgba(0,0,0,0.85)]">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`} className="flex flex-col items-center text-center px-5 py-5 md:py-6">
                  <s.icon size={20} className="text-[#C79A2B] mb-2"/>
                  {s.value ? (
                    <>
                      <p className="font-display text-2xl md:text-3xl font-extrabold leading-none" style={{ fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif", fontWeight: 800 }}>{s.value}</p>
                      <p className="text-[11px] md:text-xs text-white/60 mt-1">{s.label}</p>
                    </>
                  ) : (
                    <p className="font-display text-base md:text-lg font-extrabold leading-tight mt-1" style={{ fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif", fontWeight: 800 }}>{s.label}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ TRENDING THIS WEEK — cinematic 6-card showcase ═══════════════ */}
      <TrendingThisWeek />

      {/* ═══════════════ CINEMATIC BANNER — poster-style, text on left, scroll-reveal ═══════════════ */}
      <section
        ref={bannerRef}
        data-testid="cinematic-banner"
        data-visible={bannerVisible ? "true" : "false"}
        className="max-w-6xl mx-auto px-5 md:px-6"
        style={{ backgroundColor: "#070709", marginTop: "64px", marginBottom: "64px" }}
      >
        <div className="cinematic-banner relative w-full overflow-hidden" style={{ borderRadius: "24px" }}>
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_showcase-india/artifacts/xeqg42ik_ChatGPT%20Image%20Aug%2013%2C%202026%2C%2011_56_27%20AM.png"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="block w-full h-auto"
            style={{
              display: "block",
              transformOrigin: "center center",
              transform: bannerVisible ? "scale(1.06)" : "scale(1.00)",
              transition: "transform 3500ms ease-out",
              willChange: "transform",
            }}
          />
          {/* Subtle bottom gradient for a premium OTT feel */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: "38%",
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)",
              borderBottomLeftRadius: "24px",
              borderBottomRightRadius: "24px",
            }}
          />
          {/* Text overlay — LEFT side, poster-style, fade-up on reveal */}
          <div
            data-testid="cinematic-banner-text"
            className="absolute top-0 left-0 h-full flex flex-col justify-center pointer-events-none"
            style={{
              paddingLeft: "clamp(24px, 5vw, 72px)",
              paddingRight: "clamp(16px, 4vw, 40px)",
              maxWidth: "min(640px, 58%)",
              opacity: bannerVisible ? 1 : 0,
              transform: bannerVisible ? "translateY(0)" : "translateY(28px)",
              transition: "opacity 1100ms cubic-bezier(0.22, 1, 0.36, 1) 450ms, transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 450ms",
              willChange: "opacity, transform",
            }}
          >
            <h2
              style={{
                margin: 0,
                lineHeight: 1,
                letterSpacing: "-0.01em",
              }}
            >
              <span
                style={{
                  display: "block",
                  fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                  fontWeight: 400,
                  fontStyle: "normal",
                  color: "#F5F3EE",
                  fontSize: "clamp(44px, 6.1vw, 88px)",
                  lineHeight: 0.92,
                  letterSpacing: "-1.5px",
                  textRendering: "optimizeLegibility",
                  WebkitFontSmoothing: "antialiased",
                  textShadow: "0 0 10px rgba(255,255,255,0.06)",
                }}
              >
                From Bedroom
              </span>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Ogg Italic', 'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                  fontWeight: 500,
                  fontStyle: "italic",
                  color: "#C79A2B",
                  fontSize: "clamp(56px, 7.78vw, 112px)",
                  lineHeight: 0.88,
                  letterSpacing: "-2px",
                  textRendering: "optimizeLegibility",
                  WebkitFontSmoothing: "antialiased",
                  textShadow: "0 0 12px rgba(199,154,43,0.12), 0 0 24px rgba(199,154,43,0.08)",
                }}
              >
                to Spotlight
              </span>
            </h2>
            <p
              style={{
                marginTop: "clamp(14px, 1.6vw, 22px)",
                margin: "clamp(14px, 1.6vw, 22px) 0 0 0",
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontWeight: 400,
                color: "rgba(255,255,255,0.84)",
                fontSize: "clamp(14px, 1.11vw, 16px)",
                lineHeight: 1.55,
                letterSpacing: "0px",
                maxWidth: "340px",
              }}
            >
              One performance can change everything.
              <br />
              Upload, grow, and get discovered by the
              <br />
              entertainment industry.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════ DISCOVER YOUR CRAFT — cinematic OTT-style category strip ═══════════════ */}
      <DiscoverYourCraft />

      {/* ═══════════════ GLOBAL OPPORTUNITIES — cinematic city grid ═══════════════ */}
      <GlobalOpportunities />

      {/* ═══════════════ HOW IT WORKS — three cinematic steps to your big break ═══════════════ */}
      <HowItWorks />

      {/* ═══════════════ EXPLORE CATEGORIES ═══════════════ */}
      <section data-testid="explore-categories" className="max-w-6xl mx-auto px-5 md:px-6 pt-8 pb-20 md:pb-28">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold mb-6 md:mb-8" style={{ fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif", fontWeight: 800 }}>
          Explore Categories
        </h2>
        <div className="flex flex-wrap gap-3 md:gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`explore-${c.key}`}
              className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-[#C79A2B]/40 transition-colors px-5 py-3"
            >
              <c.icon size={16} className="text-[#C79A2B]"/>
              <span className="text-sm font-medium text-white">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ═══════════════ FINAL CTA — cinematic invitation, last major section ═══════════════ */}
      <FinalCTA />

      {/* ═══════════════ FOOTER — two-column cinematic ═══════════════ */}
      <Footer />
    </div>
  );
}

/**
 * Reference-matching Trending Talent card:
 *   - Play badge top-left
 *   - Duration chip bottom-right
 *   - Name row with verified gold check
 *   - "Category · MapPin City"
 */
function TrendingCard({ v, onGate }) {
  const duration = useMemo(() => {
    if (v.duration) {
      const s = Math.round(v.duration);
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    }
    const hash = String(v.id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return `0:${String(30 + (hash % 16)).padStart(2, "0")}`;
  }, [v.id, v.duration]);

  return (
    <button
      data-testid={`trending-card-${v.id}`}
      onClick={onGate}
      className="group text-left rounded-2xl border border-white/8 hover:border-[#C79A2B]/50 transition-all overflow-hidden bg-[#0D0C10]"
    >
      {/* Thumbnail — image only; creator name + metadata sit BELOW the frame */}
      <div className="relative aspect-[4/5] overflow-hidden">
        {v.thumbnail_url ? (
          <img src={v.thumbnail_url} alt={v.title || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#180D0D] to-black flex items-center justify-center text-[#C79A2B]">
            <PlayCircle size={38}/>
          </div>
        )}
        <span className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full bg-black/60 backdrop-blur border border-white/20 flex items-center justify-center text-white/90">
          <PlayCircle size={16}/>
        </span>
        <span className="absolute bottom-2.5 right-2.5 text-[10px] font-mono px-2 py-0.5 rounded-md bg-black/70 text-white/95 border border-white/10 backdrop-blur">
          {duration}
        </span>
      </div>
      {/* Metadata strip — sits BELOW the thumbnail (image never overlaid) */}
      <div className="px-3 md:px-3.5 pt-3 pb-3.5 border-t border-white/5">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-white truncate">{v.name || v.username || " "}</p>
          {v.is_verified && <BadgeCheck size={13} className="text-[#C79A2B] flex-shrink-0"/>}
        </div>
        <p className="mt-1 text-[11px] text-white/60 flex items-center gap-1 truncate">
          <span>{catLabel(v.category) || " "}</span>
          {v.city && (<><span className="text-white/30">·</span><MapPin size={10}/><span className="truncate">{v.city}</span></>)}
        </p>
      </div>
    </button>
  );
}


/**
 * Discover Your Craft
 * ────────────────────
 * Cinematic, OTT-style horizontal category strip with 6 cards.
 * - Left-aligned header ("EXPLORE MORE" gold label + two-line serif title)
 * - Circular arrow buttons scroll the strip left/right
 * - Cards: 240×520, cinematic image, gold border, category name + underline, icon
 * - Scroll-reveal (opacity 0→1 + translateY 40→0) fires once at 20% visibility
 * - Bottom: progress dots + "Explore more →" CTA (gates non-authed users to /signup)
 */
function DiscoverYourCraft() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const stripRef = useRef(null);
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  // Reveal-once when 20% of section is visible
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.20 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Track which card is centered for the progress dots
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const cards = strip.querySelectorAll("[data-craft-card]");
        if (!cards.length) return;
        const stripRect = strip.getBoundingClientRect();
        const stripCenter = stripRect.left + stripRect.width / 2;
        let closest = 0;
        let minDist = Infinity;
        cards.forEach((c, i) => {
          const r = c.getBoundingClientRect();
          const d = Math.abs((r.left + r.width / 2) - stripCenter);
          if (d < minDist) { minDist = d; closest = i; }
        });
        setActiveIdx(closest);
      });
    };
    strip.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { strip.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const scrollBy = (dir) => {
    const strip = stripRef.current;
    if (!strip) return;
    const card = strip.querySelector("[data-craft-card]");
    const step = card ? card.getBoundingClientRect().width + 20 : 260;
    strip.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const handleExploreMore = () => {
    if (user) nav("/discover");
    else nav("/signup");
  };

  return (
    <section
      ref={sectionRef}
      data-testid="discover-your-craft"
      className="w-full"
      style={{ backgroundColor: "#070709", paddingTop: "96px", paddingBottom: "96px" }}
    >
      <div className="mx-auto" style={{ maxWidth: "1440px", paddingLeft: "clamp(20px, 4vw, 56px)", paddingRight: "clamp(20px, 4vw, 56px)" }}>
        {/* ── HEADER ────────────────────────────────────────────── */}
        <div
          className="flex items-end justify-between gap-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <div className="flex-1 min-w-0">
            <div
              data-testid="craft-eyebrow"
              style={{
                color: "#C79A2B",
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                paddingBottom: "10px",
                borderBottom: "1px solid rgba(199,154,43,0.35)",
                width: "fit-content",
                marginBottom: "24px",
              }}
            >
              EXPLORE MORE
            </div>
            <h2 style={{ margin: 0, lineHeight: 1 }}>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                  fontWeight: 400,
                  color: "#F5F3EE",
                  fontSize: "clamp(40px, 5.55vw, 80px)",
                  lineHeight: 0.95,
                  letterSpacing: "-1.5px",
                  textRendering: "optimizeLegibility",
                  WebkitFontSmoothing: "antialiased",
                }}
              >
                Where Talent
              </span>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Ogg Italic', 'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                  fontWeight: 500,
                  fontStyle: "italic",
                  color: "#C79A2B",
                  fontSize: "clamp(44px, 6.11vw, 88px)",
                  lineHeight: 0.9,
                  letterSpacing: "-2px",
                  textRendering: "optimizeLegibility",
                  WebkitFontSmoothing: "antialiased",
                  textShadow: "0 0 12px rgba(199,154,43,0.12), 0 0 24px rgba(199,154,43,0.08)",
                  marginTop: "6px",
                }}
              >
                Finds Its Stage
              </span>
            </h2>
            <p
              style={{
                marginTop: "20px",
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "rgba(255,255,255,0.78)",
                lineHeight: 1.5,
                letterSpacing: "0px",
              }}
            >
              Choose your arena. Showcase your passion. Get discovered.
            </p>
          </div>

          {/* Arrow buttons */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0" style={{ paddingBottom: "8px" }}>
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="craft-arrow-prev"
              aria-label="Previous crafts"
              className="w-12 h-12 rounded-full flex items-center justify-center transition-colors group"
              style={{ border: "1px solid #C79A2B", background: "transparent" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#C79A2B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <ChevronLeft size={20} className="text-[#C79A2B] group-hover:text-black transition-colors"/>
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="craft-arrow-next"
              aria-label="Next crafts"
              className="w-12 h-12 rounded-full flex items-center justify-center transition-colors group"
              style={{ border: "1px solid #C79A2B", background: "transparent" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#C79A2B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <ChevronRight size={20} className="text-[#C79A2B] group-hover:text-black transition-colors"/>
            </button>
          </div>
        </div>

        {/* ── CARDS STRIP ────────────────────────────────────────── */}
        <div
          ref={stripRef}
          data-testid="craft-strip"
          className="no-scrollbar flex overflow-x-auto"
          style={{
            gap: "20px",
            marginTop: "48px",
            paddingBottom: "8px",
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, transform 900ms cubic-bezier(0.22, 1, 0.36, 1) 120ms",
          }}
        >
          {CRAFTS.map((c) => (
            <Link
              key={c.key}
              to={`/discover?category=${c.slug}`}
              data-craft-card
              data-testid={`craft-card-${c.key}`}
              className="craft-card relative flex-shrink-0 overflow-hidden group"
              style={{
                width: "clamp(200px, 22vw, 240px)",
                height: "clamp(440px, 46vw, 520px)",
                borderRadius: "24px",
                border: "1px solid rgba(199,154,43,0.35)",
                scrollSnapAlign: "start",
                background: "#0B0A0D",
              }}
            >
              {/* Background image */}
              <img
                src={c.img}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "brightness(0.78) contrast(1.05)" }}
              />
              {/* Bottom gradient for text readability */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{
                  height: "62%",
                  background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.92) 100%)",
                }}
              />
              {/* Icon (circular gold outline, lower-center) */}
              <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center" style={{ bottom: "110px" }}>
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: "52px",
                    height: "52px",
                    border: "1px solid #C79A2B",
                    background: "rgba(0,0,0,0.35)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <c.icon size={22} className="text-[#C79A2B]"/>
                </div>
              </div>
              {/* Title + gold underline */}
              <div className="absolute inset-x-0 flex flex-col items-center" style={{ bottom: "32px" }}>
                <span
                  style={{
                    fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                    color: "#F5F3EE",
                    fontWeight: 400,
                    fontSize: "clamp(22px, 2.35vw, 34px)",
                    letterSpacing: "0.04em",
                    lineHeight: 1,
                    textRendering: "optimizeLegibility",
                    WebkitFontSmoothing: "antialiased",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </span>
                <div
                  aria-hidden="true"
                  style={{
                    width: "44px",
                    height: "2px",
                    background: "#C79A2B",
                    marginTop: "10px",
                    borderRadius: "1px",
                  }}
                />
              </div>
            </Link>
          ))}
        </div>

        {/* ── PROGRESS DOTS ─────────────────────────────────────── */}
        <div
          className="flex items-center justify-center"
          style={{ marginTop: "40px", gap: "10px" }}
          data-testid="craft-progress"
        >
          {CRAFTS.map((_, i) => (
            <span
              key={i}
              aria-hidden="true"
              style={{
                display: "inline-block",
                height: "2px",
                width: i === activeIdx ? "44px" : "28px",
                background: i === activeIdx ? "#C79A2B" : "rgba(255,255,255,0.18)",
                transition: "width 300ms ease, background 300ms ease",
                borderRadius: "1px",
              }}
            />
          ))}
        </div>

        {/* ── CTA ───────────────────────────────────────────────── */}
        <div className="flex flex-col items-center" style={{ marginTop: "24px" }}>
          <button
            type="button"
            onClick={handleExploreMore}
            data-testid="craft-explore-more"
            className="inline-flex items-center gap-2 transition-opacity hover:opacity-80"
            style={{
              color: "#C79A2B",
              background: "transparent",
              border: "none",
              fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
              fontSize: "15px",
              fontWeight: 500,
              letterSpacing: "0.02em",
              cursor: "pointer",
              padding: "8px 4px",
            }}
          >
            Explore more
            <ArrowRight size={16}/>
          </button>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Global Opportunities — cinematic city cards over a glowing earth backdrop.
// ═══════════════════════════════════════════════════════════════════════
const CITIES_ROW1 = [
  { key: "ujjain",   name: "Ujjain",   country: "India",          img: "/cities/ujjain.jpg" },
  { key: "mumbai",   name: "Mumbai",   country: "India",          img: "/cities/mumbai.jpg" },
  { key: "dubai",    name: "Dubai",    country: "UAE",            img: "/cities/dubai.jpg" },
  { key: "london",   name: "London",   country: "United Kingdom", img: "/cities/london.jpg" },
  { key: "newyork",  name: "New York", country: "USA",            img: "/cities/newyork.jpg" },
  { key: "toronto",  name: "Toronto",  country: "Canada",         img: "/cities/toronto.jpg" },
  { key: "sydney",   name: "Sydney",   country: "Australia",      img: "/cities/sydney.jpg" },
];

const CITIES_ROW2 = [
  { key: "la",        name: "Los Angeles", country: "USA",         img: "/cities/la.jpg" },
  { key: "chicago",   name: "Chicago",     country: "USA",         img: "/cities/chicago.jpg" },
  { key: "berlin",    name: "Berlin",      country: "Germany",     img: "/cities/berlin.jpg" },
  { key: "tokyo",     name: "Tokyo",       country: "Japan",       img: "/cities/tokyo.jpg" },
  { key: "seoul",     name: "Seoul",       country: "South Korea", img: "/cities/seoul.jpg" },
  { key: "amsterdam", name: "Amsterdam",   country: "Netherlands", img: "/cities/amsterdam.jpg" },
  { key: "barcelona", name: "Barcelona",   country: "Spain",       img: "/cities/barcelona.jpg" },
  { key: "rome",      name: "Rome",        country: "Italy",       img: "/cities/rome.jpg" },
  { key: "bangkok",   name: "Bangkok",     country: "Thailand",    img: "/cities/bangkok.jpg" },
  { key: "istanbul",  name: "Istanbul",    country: "Türkiye",     img: "/cities/istanbul.jpg" },
];

function CityCard({ city, widthPx, heightPx, radiusPx = 22, size = "lg" }) {
  const ref = useRef(null);
  // Mobile tap → briefly show active state for 600ms
  const onTouchStart = () => {
    const el = ref.current;
    if (!el) return;
    el.classList.add("is-active");
    window.setTimeout(() => el.classList.remove("is-active"), 600);
  };

  const nameSize = size === "sm"
    ? "18px"
    : "30px";
  const countrySize = size === "sm"
    ? "12px"
    : "15px";
  const bottomInset = size === "sm" ? "10px" : "16px";
  const dividerMt   = size === "sm" ? "4px" : "6px";
  const dividerMb   = size === "sm" ? "5px" : "8px";
  const gradientHeight = size === "sm" ? "70%" : "62%";

  return (
    <div
      ref={ref}
      tabIndex={0}
      role="button"
      onTouchStart={onTouchStart}
      data-testid={`city-card-${city.key}`}
      className="city-card relative overflow-hidden flex-shrink-0"
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        borderRadius: `${radiusPx}px`,
        border: "1px solid rgba(199,154,43,0.35)",
        background: "#0B0A0D",
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
      }}
    >
      <img
        src={city.img}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* No overlay / no text — crops from the approved mockup already contain
          the cinematic dark gradient and baked city/country labels. */}
    </div>
  );
}

function GlobalOpportunities() {
  return (
    <section
      data-testid="global-opportunities"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "#070709", paddingTop: "96px", paddingBottom: "96px" }}
    >
      {/* Glowing earth backdrop — top-right, low opacity */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: "-8%",
          right: "-14%",
          width: "clamp(560px, 55vw, 900px)",
          height: "clamp(560px, 55vw, 900px)",
          background:
            "radial-gradient(circle at 42% 45%, rgba(199,154,43,0.55) 0%, rgba(199,154,43,0.22) 22%, rgba(0,0,0,0) 55%)",
          filter: "blur(2px)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: "-4%",
          right: "-10%",
          width: "clamp(520px, 50vw, 820px)",
          height: "clamp(520px, 50vw, 820px)",
          borderRadius: "50%",
          background:
            "url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1400&q=70') center/cover no-repeat",
          mixBlendMode: "screen",
          opacity: 0.4,
          filter: "brightness(0.75)",
          maskImage: "radial-gradient(circle at center, black 55%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(circle at center, black 55%, transparent 78%)",
        }}
      />
      {/* Left-edge black gradient so text remains dominant over the globe */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 left-0 pointer-events-none"
        style={{
          width: "60%",
          background: "linear-gradient(90deg, rgba(7,7,9,0.95) 0%, rgba(7,7,9,0.7) 35%, rgba(7,7,9,0) 100%)",
        }}
      />

      <div className="relative mx-auto" style={{ maxWidth: "1440px", paddingLeft: "clamp(20px, 4vw, 56px)", paddingRight: "clamp(20px, 4vw, 56px)" }}>
        {/* ── HEADER ─────────────────────────────────────────────── */}
        <div style={{ maxWidth: "min(760px, 60%)" }}>
          <div
            data-testid="global-eyebrow"
            style={{
              color: "#C79A2B",
              fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
              fontSize: "clamp(12px, 0.97vw, 14px)",
              fontWeight: 500,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              paddingBottom: "10px",
              borderBottom: "1px solid rgba(199,154,43,0.35)",
              width: "fit-content",
              marginBottom: "24px",
            }}
          >
            GLOBAL OPPORTUNITIES
          </div>
          <h2 style={{ margin: 0, lineHeight: 1 }}>
            <span
              style={{
                display: "block",
                fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                fontWeight: 400,
                color: "#F5F3EE",
                fontSize: "clamp(42px, 5.69vw, 82px)",
                lineHeight: 0.92,
                letterSpacing: "-1.5px",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased",
                whiteSpace: "nowrap",
              }}
            >
              The World Awaits
            </span>
            <span
              style={{
                display: "block",
                fontFamily: "'Ogg Italic', 'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                fontWeight: 500,
                fontStyle: "italic",
                color: "#C79A2B",
                fontSize: "clamp(48px, 6.39vw, 92px)",
                lineHeight: 0.88,
                letterSpacing: "-2px",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased",
                textShadow: "0 0 12px rgba(199,154,43,0.12), 0 0 24px rgba(199,154,43,0.08)",
                marginTop: "6px",
              }}
            >
              Your Talent
            </span>
          </h2>
          <p
            style={{
              marginTop: "20px",
              fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
              fontSize: "clamp(16px, 1.39vw, 20px)",
              fontWeight: 400,
              color: "rgba(255,255,255,0.84)",
              lineHeight: 1.6,
              letterSpacing: "0px",
              maxWidth: "520px",
            }}
          >
            Explore opportunities in cities around the globe.
            <br />
            Choose your destination. Begin your journey.
          </p>
        </div>
      </div>

      {/* ── Rows escape the 1440px container so 220×420 cards fit at natural size ── */}
      <div
        className="relative no-scrollbar"
        style={{
          marginTop: "56px",
          width: "100%",
          overflowX: "auto",
          paddingBottom: "4px",
        }}
      >
        <div
          data-testid="global-row-1"
          className="flex"
          style={{
            gap: "18px",
            justifyContent: "safe center",
            paddingInline: "clamp(20px, 4vw, 56px)",
            minWidth: "max-content",
          }}
        >
          {CITIES_ROW1.map((city) => (
            <CityCard key={city.key} city={city} widthPx={220} heightPx={420} radiusPx={22} size="lg" />
          ))}
        </div>
      </div>

      <div
        className="relative no-scrollbar"
        style={{
          marginTop: "18px",
          width: "100%",
          overflowX: "auto",
          paddingBottom: "4px",
        }}
      >
        <div
          data-testid="global-row-2"
          className="flex"
          style={{
            gap: "18px",
            justifyContent: "safe center",
            paddingInline: "clamp(20px, 4vw, 56px)",
            minWidth: "max-content",
          }}
        >
          {CITIES_ROW2.map((city) => (
            <CityCard key={city.key} city={city} widthPx={150} heightPx={210} radiusPx={22} size="sm" />
          ))}
        </div>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// How It Works — three cinematic steps to your big break.
// Uses direct crops from the approved mockup for pixel-perfect fidelity.
// Each step column reveals with a staggered translateY + fade-in.
// ═══════════════════════════════════════════════════════════════════════
function HowItWorks() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.20 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const stepBase = "block w-full h-auto";
  const stepStyle = (delayMs) => ({
    display: "block",
    width: "100%",
    height: "auto",
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(60px)",
    transition: `opacity 850ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms, transform 850ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`,
    willChange: "opacity, transform",
  });

  return (
    <section
      ref={ref}
      data-testid="how-it-works"
      className="w-full"
      style={{
        backgroundColor: "#070709",
        opacity: visible ? 1 : 0,
        transform: visible ? "scale(1)" : "scale(1.03)",
        transformOrigin: "center center",
        transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: "1628px" }}>
        {/* Header: label + heading + subtitle (baked into image) */}
        <img
          data-testid="how-it-works-header"
          src="/how-it-works/header.jpg"
          alt="How It Works — Three Simple Steps To Your Big Break. From uploading your talent to getting discovered by industry experts. We make it simple, powerful, and possible."
          loading="lazy"
          className="block w-full h-auto"
        />
        {/* Three step columns — pixel-aligned crops from the same mockup */}
        <div
          data-testid="how-it-works-steps"
          className="grid"
          style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
        >
          <img
            data-testid="how-it-works-step-01"
            src="/how-it-works/step1.jpg"
            alt="Step 01 — Upload. Upload your performance in just seconds."
            loading="lazy"
            className={stepBase}
            style={stepStyle(0)}
          />
          <img
            data-testid="how-it-works-step-02"
            src="/how-it-works/step2.jpg"
            alt="Step 02 — Build. Create your professional profile. Add skills, photos, and express your craft."
            loading="lazy"
            className={stepBase}
            style={stepStyle(180)}
          />
          <img
            data-testid="how-it-works-step-03"
            src="/how-it-works/step3.jpg"
            alt="Step 03 — Get Discovered. Get discovered by industry experts."
            loading="lazy"
            className={stepBase}
            style={stepStyle(360)}
          />
        </div>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Final CTA — cinematic closing invitation. Warm spotlight, gold accents,
// staggered scroll-reveal. Last major section before the footer.
// ═══════════════════════════════════════════════════════════════════════
function FinalCTA() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.20 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const revealStyle = (delayMs, translatePx = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : `translateY(${translatePx}px)`,
    transition: `opacity 850ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms, transform 850ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`,
    willChange: "opacity, transform",
  });

  const handleJoin = () => {
    if (user) nav("/creator/onboarding");
    else nav("/signup");
  };

  return (
    <section
      ref={ref}
      data-testid="final-cta"
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "#070709",
        minHeight: "900px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "96px",
        paddingBottom: "96px",
        opacity: visible ? 1 : 0,
        transform: visible ? "scale(1)" : "scale(1.02)",
        transformOrigin: "center center",
        transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      {/* Warm cinematic spotlight from top-center */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: "-25%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1200px",
          height: "1000px",
          background:
            "radial-gradient(ellipse at center top, rgba(199,154,43,0.20) 0%, rgba(199,154,43,0.12) 22%, rgba(199,154,43,0.04) 45%, rgba(7,7,9,0) 70%)",
          filter: "blur(4px)",
        }}
      />
      {/* Subtle curved orbital lines — bottom-left */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          bottom: "-320px",
          left: "-240px",
          width: "820px",
          height: "820px",
          borderRadius: "50%",
          border: "1px solid rgba(199,154,43,0.14)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          bottom: "-380px",
          left: "-180px",
          width: "640px",
          height: "640px",
          borderRadius: "50%",
          border: "1px solid rgba(199,154,43,0.10)",
        }}
      />
      {/* Subtle curved orbital lines — bottom-right */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          bottom: "-320px",
          right: "-240px",
          width: "820px",
          height: "820px",
          borderRadius: "50%",
          border: "1px solid rgba(199,154,43,0.14)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          bottom: "-380px",
          right: "-180px",
          width: "640px",
          height: "640px",
          borderRadius: "50%",
          border: "1px solid rgba(199,154,43,0.10)",
        }}
      />

      {/* ── Centered content ─────────────────────────────────────── */}
      <div
        className="relative flex flex-col items-center text-center"
        style={{
          maxWidth: "980px",
          width: "100%",
          paddingInline: "clamp(20px, 4vw, 40px)",
        }}
      >
        {/* Heading */}
        <div style={revealStyle(0, 32)}>
          <span
            style={{
              display: "block",
              fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
              fontWeight: 400,
              color: "#F5F3EE",
              fontSize: "clamp(44px, 5vw, 72px)",
              lineHeight: 0.92,
              letterSpacing: "-1px",
              textRendering: "optimizeLegibility",
              WebkitFontSmoothing: "antialiased",
            }}
          >
            Your
          </span>
          <span
            style={{
              display: "block",
              fontFamily: "'Ogg Italic', 'Cormorant Garamond', 'Playfair Display', Georgia, serif",
              fontWeight: 500,
              fontStyle: "italic",
              color: "#C79A2B",
              fontSize: "clamp(58px, 6.67vw, 96px)",
              lineHeight: 0.88,
              letterSpacing: "-2px",
              textRendering: "optimizeLegibility",
              WebkitFontSmoothing: "antialiased",
              textShadow: "0 0 12px rgba(199,154,43,0.18), 0 0 28px rgba(199,154,43,0.10)",
              marginTop: "6px",
            }}
          >
            NextScene
          </span>
          <span
            style={{
              display: "block",
              fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
              fontWeight: 400,
              color: "#F5F3EE",
              fontSize: "clamp(62px, 7.64vw, 110px)",
              lineHeight: 0.9,
              letterSpacing: "-2px",
              textRendering: "optimizeLegibility",
              WebkitFontSmoothing: "antialiased",
              marginTop: "6px",
            }}
          >
            Starts Here
          </span>
        </div>

        {/* Gold divider with centered sparkle */}
        <div
          data-testid="final-cta-divider"
          className="flex flex-col items-center"
          style={{
            marginTop: "28px",
            opacity: visible ? 1 : 0,
            transition: "opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) 120ms",
          }}
        >
          <Sparkles size={12} className="text-[#C79A2B]" />
          <div
            aria-hidden="true"
            style={{
              width: "280px",
              height: "1px",
              background: "rgba(199,154,43,0.55)",
              marginTop: "12px",
            }}
          />
        </div>

        {/* Subtext */}
        <p
          data-testid="final-cta-subtext"
          style={{
            ...revealStyle(180, 24),
            marginTop: "20px",
            maxWidth: "640px",
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 400,
            fontSize: "clamp(16px, 1.53vw, 22px)",
            lineHeight: 1.65,
            color: "rgba(255,255,255,0.82)",
            letterSpacing: "0px",
          }}
        >
          One performance can open the door to your next opportunity.
        </p>

        {/* CTA Button */}
        <div
          style={{
            ...revealStyle(260, 20),
            marginTop: "36px",
            position: "relative",
          }}
        >
          {/* Faint reflective floor glow under the button */}
          <div
            aria-hidden="true"
            className="absolute pointer-events-none"
            style={{
              left: "50%",
              bottom: "-24px",
              transform: "translateX(-50%)",
              width: "440px",
              height: "60px",
              background: "radial-gradient(ellipse at center, rgba(199,154,43,0.22) 0%, rgba(199,154,43,0.06) 45%, rgba(7,7,9,0) 75%)",
              filter: "blur(2px)",
            }}
          />
          <button
            type="button"
            onClick={handleJoin}
            data-testid="final-cta-join"
            className="final-cta-btn relative inline-flex items-center justify-center"
            style={{
              width: "clamp(260px, 25vw, 360px)",
              height: "clamp(64px, 5.28vw, 76px)",
              borderRadius: "999px",
              background: "#C79A2B",
              color: "#070709",
              fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: "clamp(18px, 1.67vw, 24px)",
              letterSpacing: "0.2px",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(199,154,43,0.28), 0 0 28px rgba(199,154,43,0.18)",
              border: "none",
              cursor: "pointer",
              transition: "transform 280ms ease, background 280ms ease",
            }}
          >
            Join NextScene
            <ArrowRight size={24} />
          </button>
        </div>

        {/* Bottom category line */}
        <div
          data-testid="final-cta-categories"
          style={{
            ...revealStyle(360, 0),
            marginTop: "72px",
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 500,
            fontSize: "clamp(12px, 1.04vw, 15px)",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: "rgba(199,154,43,0.92)",
            textAlign: "center",
          }}
        >
          ACTORS &nbsp;•&nbsp; SINGERS &nbsp;•&nbsp; DANCERS &nbsp;•&nbsp; CREATORS &nbsp;•&nbsp; VOICE ARTISTS &nbsp;•&nbsp; &amp; MANY MORE
        </div>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Trending This Week — cinematic 6-card showcase directly cropped from the
// approved mockup. Each card contains a baked-in portrait, flag, play icon,
// title, role and view-count. We layer a lightweight play button + gold
// glow overlay so hover state feels premium, and stagger the reveal.
// ═══════════════════════════════════════════════════════════════════════
const TRENDING_CARDS = [
  { key: "singer",       img: "/trending/singer.jpg" },
  { key: "dancer",       img: "/trending/dancer.jpg" },
  { key: "actor",        img: "/trending/actor.jpg" },
  { key: "voice-artist", img: "/trending/voice-artist.jpg" },
  { key: "model",        img: "/trending/model.jpg" },
  { key: "guitarist",    img: "/trending/guitarist.jpg" },
];

function TrendingThisWeek() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq && mq.matches) setReduced(true);
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const cardStyle = (i) => {
    const delay = i * 120;
    if (reduced) return { opacity: 1, transform: "none" };
    return {
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0) scale(1)" : "translateY(80px) scale(0.94)",
      transition: `opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      willChange: "opacity, transform",
    };
  };

  return (
    <section
      ref={ref}
      data-testid="trending-this-week"
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "#070709",
        paddingTop: "120px",
        paddingBottom: "110px",
        opacity: visible || reduced ? 1 : 0,
        transform: (visible || reduced) ? "scale(1)" : "scale(1.03)",
        transformOrigin: "center center",
        transition: "opacity 1000ms cubic-bezier(0.22, 1, 0.36, 1), transform 1000ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      {/* Warm top-center spotlight */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: "-18%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1200px",
          height: "760px",
          background:
            "radial-gradient(ellipse at center top, rgba(199,154,43,0.18) 0%, rgba(199,154,43,0.10) 25%, rgba(199,154,43,0.03) 55%, rgba(7,7,9,0) 78%)",
          filter: "blur(3px)",
        }}
      />
      {/* Subtle floor reflection glow */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1080px",
          height: "80px",
          background:
            "radial-gradient(ellipse at center, rgba(199,154,43,0.20) 0%, rgba(199,154,43,0.06) 45%, rgba(7,7,9,0) 78%)",
          filter: "blur(2px)",
        }}
      />
      {/* Faint soft vignette */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(7,7,9,0) 55%, rgba(7,7,9,0.55) 100%)",
        }}
      />

      <div className="relative mx-auto" style={{ maxWidth: "1560px", paddingInline: "clamp(20px, 3vw, 48px)" }}>
        {/* Heading */}
        <h2
          data-testid="trending-heading"
          className="text-center"
          style={{
            margin: 0,
            fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
            fontWeight: 400,
            fontSize: "clamp(38px, 6.39vw, 92px)",
            lineHeight: 0.95,
            letterSpacing: "-2px",
            textRendering: "optimizeLegibility",
            WebkitFontSmoothing: "antialiased",
            opacity: (visible || reduced) ? 1 : 0,
            transform: (visible || reduced) ? "translateY(0)" : "translateY(50px)",
            transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "opacity, transform",
          }}
        >
          <span style={{ color: "#F5F3EE" }}>Trending </span>
          <span
            style={{
              color: "#C79A2B",
              textShadow: "0 0 10px rgba(199,154,43,0.12), 0 0 22px rgba(199,154,43,0.08)",
            }}
          >
            This Week
          </span>
        </h2>

        {/* Decorative gold divider with sparkle */}
        <div
          data-testid="trending-divider"
          className="flex flex-col items-center"
          style={{
            marginTop: "22px",
            marginBottom: "40px",
            opacity: (visible || reduced) ? 1 : 0,
            transform: (visible || reduced) ? "scaleX(1)" : "scaleX(0.6)",
            transition: "opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, transform 700ms cubic-bezier(0.22, 1, 0.36, 1) 120ms",
          }}
        >
          <Sparkles size={14} className="text-[#C79A2B]" />
          <div
            aria-hidden="true"
            style={{
              width: "220px",
              height: "1px",
              background: "rgba(199,154,43,0.55)",
              marginTop: "10px",
            }}
          />
        </div>

        {/* 6-card cinematic strip */}
        <div
          data-testid="trending-rail"
          className="no-scrollbar flex"
          style={{
            gap: "24px",
            justifyContent: "safe center",
            overflowX: "auto",
            paddingBottom: "8px",
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {TRENDING_CARDS.map((c, i) => (
            <TrendingCardV2 key={c.key} card={c} style={cardStyle(i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrendingCardV2({ card, style }) {
  return (
    <div
      data-testid={`trending-card-${card.key}`}
      className="trending-card relative flex-shrink-0 overflow-hidden group"
      style={{
        width: "236px",
        height: "620px",
        borderRadius: "28px",
        border: "1px solid rgba(199,154,43,0.42)",
        background: "#0B0A0D",
        boxShadow: "inset 0 0 30px rgba(0,0,0,0.55), 0 0 0 rgba(199,154,43,0), 0 10px 30px rgba(0,0,0,0.45)",
        scrollSnapAlign: "start",
        ...style,
      }}
    >
      <img
        src={card.img}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Slight gold edge glow — brightens on hover (uses .trending-card:hover in CSS) */}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Login modal (top-nav) — dark cinematic sheet with 4 auth providers +
// inline mobile OTP flow. Google uses the existing Emergent redirect.
// Apple & Facebook are UI-only placeholders (SSO not yet integrated).
// ═══════════════════════════════════════════════════════════════════════
function LoginModal({ open, onClose }) {
  const nav = useNavigate();
  const { setUser } = useAuth() || {};
  const [view, setView] = useState("choose"); // choose | mobile-phone | mobile-otp
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpHint, setOtpHint] = useState("");

  // Lock body scroll while open; reset when closed.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  useEffect(() => {
    if (!open) { setView("choose"); setPhone(""); setOtp(""); setOtpHint(""); }
  }, [open]);

  if (!open) return null;

  const goGoogle = () => openGoogleRedirect({ redirectTo: "/app" });
  const soonToast = (name) => toast.info(`${name} sign-in coming soon`);

  const sendOtp = async () => {
    if (phone.length !== 10) return toast.error("Enter your 10-digit mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone_number: `+91${phone}` });
      setView("mobile-otp");
      setOtpHint(data.hint || "");
      toast.success(data.hint ? "OTP sent (MOCK)" : "OTP sent to your phone");
    } catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", {
        phone_number: `+91${phone}`, code: otp, terms_accepted: true,
      });
      localStorage.setItem("ns_token", data.token);
      setUser && setUser(data.user);
      toast.success(data.is_new ? "Welcome to NextScene!" : "Welcome back!");
      onClose();
      nav(data.needs_onboarding ? "/app/onboarding" : "/app");
    } catch (err) { toast.error(errMsg(err, "Invalid OTP")); }
    finally { setBusy(false); }
  };

  // ── Presentational atoms ────────────────────────────────────────────
  const providerBtnStyle = {
    width: "100%",
    height: "52px",
    borderRadius: "16px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#F5F3EE",
    fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
    fontWeight: 500,
    fontSize: "15px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    position: "relative",
    cursor: "pointer",
  };
  const iconLeft = { position: "absolute", left: "18px", top: "50%", transform: "translateY(-50%)", display: "inline-flex" };

  return (
    <div
      data-testid="login-modal-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.68)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
    >
      <div
        data-testid="login-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        className="relative"
        style={{
          width: "min(420px, calc(100vw - 32px))",
          background: "#0B0B0D",
          border: "1px solid rgba(199,154,43,0.18)",
          borderRadius: "28px",
          padding: "32px",
          boxShadow: "0 30px 80px rgba(0,0,0,0.65), 0 0 40px rgba(199,154,43,0.10)",
        }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          data-testid="login-modal-close"
          aria-label="Close login modal"
          className="absolute inline-flex items-center justify-center"
          style={{ top: "18px", right: "18px", width: "32px", height: "32px", background: "transparent", border: "none", color: "rgba(255,255,255,0.72)", cursor: "pointer" }}
        >
          <X size={20} />
        </button>

        {view === "choose" && (
          <>
            <h2
              id="login-modal-title"
              className="text-center"
              style={{
                margin: 0,
                fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif",
                fontWeight: 400,
                color: "#F5F3EE",
                fontSize: "34px",
                lineHeight: 1.1,
                letterSpacing: "-0.5px",
              }}
            >
              Welcome to <span style={{ color: "#C79A2B" }}>nextscene</span>
            </h2>
            <p
              className="text-center"
              style={{
                marginTop: "6px",
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontSize: "15px",
                color: "rgba(255,255,255,0.72)",
                fontWeight: 400,
              }}
            >
              Continue your journey
            </p>

            <div className="flex flex-col" style={{ marginTop: "24px", gap: "12px" }}>
              <button type="button" data-testid="login-provider-google" className="ns-login-provider" style={providerBtnStyle} onClick={goGoogle}>
                <span style={iconLeft}>
                  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </span>
                Continue with Google
              </button>

              <button type="button" data-testid="login-provider-apple" className="ns-login-provider" style={providerBtnStyle} onClick={() => soonToast("Apple")}>
                <span style={iconLeft}>
                  <svg width="20" height="20" viewBox="0 0 384 512" aria-hidden="true">
                    <path fill="#F5F3EE" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM259.2 108.1c22.7-27 20.6-51.6 19.9-60.5-20 1.2-43.1 13.6-56.3 28.9-14.5 16.4-23 36.7-21.2 58.9 21.6 1.7 41.3-9.4 57.6-27.3z"/>
                  </svg>
                </span>
                Continue with Apple
              </button>

              <button type="button" data-testid="login-provider-facebook" className="ns-login-provider" style={providerBtnStyle} onClick={() => soonToast("Facebook")}>
                <span style={iconLeft}>
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#1877F2" d="M24 12.073C24 5.446 18.627.073 12 .073S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.063 24 12.073z"/>
                  </svg>
                </span>
                Continue with Facebook
              </button>

              <button type="button" data-testid="login-provider-mobile" className="ns-login-provider" style={providerBtnStyle} onClick={() => setView("mobile-phone")}>
                <span style={iconLeft}>
                  <Smartphone size={20} color="#C79A2B" />
                </span>
                Continue with Mobile Number
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center" style={{ marginTop: "20px", marginBottom: "12px", gap: "12px" }}>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.08)" }} />
              <span style={{ fontFamily: "'Inter'", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>or</span>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.08)" }} />
            </div>

            <p className="text-center" style={{ margin: 0, fontFamily: "'Inter'", fontSize: "13px", color: "rgba(255,255,255,0.68)" }}>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setView("mobile-phone")}
                data-testid="login-modal-signin-link"
                style={{ background: "transparent", border: "none", color: "#C79A2B", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: "inherit", fontWeight: 500 }}
                className="hover:underline"
              >
                Sign In
              </button>
            </p>
          </>
        )}

        {view === "mobile-phone" && (
          <>
            <h2 className="text-center" style={{ margin: 0, fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Playfair Display', Georgia, serif", fontWeight: 400, color: "#F5F3EE", fontSize: "28px", lineHeight: 1.15 }}>
              Enter your mobile
            </h2>
            <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter'", fontSize: "14px", color: "rgba(255,255,255,0.72)" }}>
              We'll send you a 6-digit OTP
            </p>
            <div className="flex items-center" style={{ marginTop: "22px", gap: "10px" }}>
              <div style={{ height: "52px", padding: "0 14px", display: "inline-flex", alignItems: "center", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#F5F3EE", fontFamily: "'Inter'", fontSize: "15px" }}>+91</div>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="10-digit mobile"
                data-testid="login-modal-phone"
                autoFocus
                style={{
                  flex: 1,
                  height: "52px",
                  padding: "0 14px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.03)",
                  color: "#F5F3EE",
                  fontFamily: "'Inter'",
                  fontSize: "15px",
                  outline: "none",
                }}
                onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }}
              />
            </div>
            <button
              type="button"
              onClick={sendOtp}
              disabled={busy || phone.length !== 10}
              data-testid="login-modal-send-otp"
              className="final-cta-btn"
              style={{
                width: "100%", marginTop: "18px", height: "52px", borderRadius: "999px",
                background: phone.length === 10 && !busy ? "#C79A2B" : "rgba(199,154,43,0.35)",
                color: "#070709", fontFamily: "'Inter'", fontWeight: 600, fontSize: "15px",
                border: "none", cursor: phone.length === 10 && !busy ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
            >
              {busy ? <Loader2 size={18} className="animate-spin"/> : null}
              Send OTP
            </button>
            <button
              type="button"
              onClick={() => setView("choose")}
              data-testid="login-modal-back"
              style={{ width: "100%", marginTop: "12px", background: "transparent", border: "none", color: "rgba(255,255,255,0.68)", fontFamily: "'Inter'", fontSize: "13px", cursor: "pointer" }}
              className="hover:underline"
            >
              ← Back to sign-in options
            </button>
          </>
        )}

        {view === "mobile-otp" && (
          <>
            <h2 className="text-center" style={{ margin: 0, fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Playfair Display', Georgia, serif", fontWeight: 400, color: "#F5F3EE", fontSize: "28px", lineHeight: 1.15 }}>
              Enter OTP
            </h2>
            <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter'", fontSize: "14px", color: "rgba(255,255,255,0.72)" }}>
              Code sent to +91 {phone}
            </p>
            {otpHint && (
              <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter'", fontSize: "12px", color: "#F4C550" }}>
                MOCK: use {otpHint.replace(/^MOCK OTP:\s*/, "")}
              </p>
            )}
            <input
              type="tel"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit code"
              data-testid="login-modal-otp"
              autoFocus
              style={{
                width: "100%", marginTop: "22px", height: "56px", padding: "0 16px",
                borderRadius: "12px", border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)", color: "#F5F3EE",
                fontFamily: "'Inter'", fontSize: "20px", letterSpacing: "0.35em",
                textAlign: "center", outline: "none",
              }}
              onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(); }}
            />
            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy || otp.length !== 6}
              data-testid="login-modal-verify-otp"
              className="final-cta-btn"
              style={{
                width: "100%", marginTop: "18px", height: "52px", borderRadius: "999px",
                background: otp.length === 6 && !busy ? "#C79A2B" : "rgba(199,154,43,0.35)",
                color: "#070709", fontFamily: "'Inter'", fontWeight: 600, fontSize: "15px",
                border: "none", cursor: otp.length === 6 && !busy ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
            >
              {busy ? <Loader2 size={18} className="animate-spin"/> : null}
              Verify &amp; Continue
            </button>
            <button
              type="button"
              onClick={() => { setView("mobile-phone"); setOtp(""); }}
              data-testid="login-modal-change-phone"
              style={{ width: "100%", marginTop: "12px", background: "transparent", border: "none", color: "rgba(255,255,255,0.68)", fontFamily: "'Inter'", fontSize: "13px", cursor: "pointer" }}
              className="hover:underline"
            >
              ← Change mobile number
            </button>
          </>
        )}
      </div>
    </div>
  );
}

